import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  IconButton,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { styled } from '@mui/material/styles';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { ProviderServicePricing } from '../../../data/models/provider-service-pricing.model';
import {
  AccountingState,
  PricingModel,
} from '../../../data/models/pricing-model.model';
import * as referenceSlice from '../../../data/reference.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as providerSlice from '../../../data/provider.slice';
import providerUtils from '../../../services/providerUtils';
import stringUtils from '../../../services/stringUtils';
import { CoverageArea } from '../../../data/models/coverage-area.model';
import { PricingFunction } from '../../../data/models/pricing-function.model';

const PREFIX = 'ProviderPricingList';

const classes = {
  container: `${PREFIX}-container`,
  panelTools: `${PREFIX}-panelTools`,
  panelTitle: `${PREFIX}-panelTitle`,
  summary: `${PREFIX}-summary`,
  details: `${PREFIX}-details`,
  label: `${PREFIX}-label`,
  chips: `${PREFIX}-chips`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    gap: theme.spacing(1),
    margin: theme.spacing(2),
  },

  [`& .${classes.panelTools}`]: {
    width: '100%',
  },

  [`& .${classes.panelTitle}`]: {
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
  },

  [`& .${classes.summary}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },

  [`& .${classes.details}`]: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
  },

  [`& .${classes.chips}`]: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
}));

interface ProviderPricingListProps {
  providerServicePricings: ProviderServicePricing[];
  pricingModels: PricingModel[];
  onEdit: (evt: any, providerServicePricing: ProviderServicePricing) => void;
  onActivate: (evt: any, psp: ProviderServicePricing) => void;
  onDeactivate: (evt: any, psp: ProviderServicePricing) => void;
  onDelete: (evt: any, providerServicePricing: ProviderServicePricing) => void;
  readonly: boolean;
}

export default function ProviderPricingList({
  providerServicePricings,
  pricingModels,
  onEdit,
  onActivate,
  onDeactivate,
  onDelete,
  readonly,
}: ProviderPricingListProps) {
  const t = useCommonTranslation();

  const chargeTypes = useAppSelector(referenceSlice.selectChargeTypes);
  const coverageAreas = useAppSelector(providerSlice.selectCoverageAreas);

  const coverageAreasMap = coverageAreas.reduce(
    (map, coverageArea) => {
      map[coverageArea.coverageAreaID!] = coverageArea;
      return map;
    },
    {} as Record<number, CoverageArea>,
  );

  const pricingFunctions = useAppSelector(
    accountingSlice.selectAllPricingFunctions,
  );

  const transportServices = useAppSelector(
    accountingSlice.selectAllTransportationServices,
  );

  const handleActiveChange = (
    evt: any,
    providerServicePricing: ProviderServicePricing,
  ) => {
    if (evt.target.checked) {
      onActivate(evt, providerServicePricing);
    } else {
      onDeactivate(evt, providerServicePricing);
    }
  };

  const getTransportServiceName = (id: string) => {
    const ts = transportServices.find(
      (t) => t.transportationServiceUUID === id,
    );
    return ts ? ts.name : '';
  };

  const getPricingModelName = (id: string) => {
    const pm = pricingModels.find((p) => p.pricingModelUUID === id);
    return pm ? pm.name : '';
  };

  const getChargeTypeName = (id: string) => {
    const ct = chargeTypes.find((c) => c.chargeTypeBC === id);
    return ct ? ct.chargeTypeName : '';
  };

  return (
    <StyledBox className={classes.container}>
      {providerServicePricings.map((servicePricing) => {
        const isActive = servicePricing.state === AccountingState.Active;
        const isInactive = servicePricing.state === AccountingState.Inactive;
        const stateLabel = t(providerUtils.getStateLabel(servicePricing.state));

        const coverageAreas = servicePricing.coverageAreaIDs.map(
          (id) => coverageAreasMap[id],
        );

        return (
          <Accordion key={servicePricing.providerServicePricingUUID}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box className={classes.panelTools}>
                <Box className={classes.summary}>
                  <Box className={classes.panelTitle}>
                    {servicePricing.name}
                    <Chip label={stateLabel} />
                  </Box>
                  {!readonly && (
                    <Box>
                      <Tooltip title={t('provider.editServicePricing')}>
                        <span>
                          <IconButton
                            data-testid="edit-contact"
                            onClick={(evt) => onEdit(evt, servicePricing)}
                            disabled={isInactive || isActive}
                            color="primary"
                            size="large">
                            <EditOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={t('provider.deleteServicePricing')}>
                        <span>
                          <IconButton
                            data-testid="delete-pricing-model"
                            disabled={isActive}
                            onClick={(evt) => onDelete(evt, servicePricing)}
                            color="error"
                            size="large">
                            <DeleteForeverIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={stateLabel}>
                        <span>
                          <Switch
                            checked={isActive}
                            onChange={(evt) =>
                              handleActiveChange(evt, servicePricing)
                            }
                            disabled={isInactive}
                            onClick={(evt) => evt.stopPropagation()}
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box className={classes.details}>
                <Grid container spacing={1}>
                  <Grid item xs={12} className={classes.panelTitle}>
                    {t('provider.transportationServices')}
                  </Grid>
                  <Grid item xs={12} className={classes.chips}>
                    {servicePricing.transportationServiceUUIDs?.map(
                      (tsuuid) => {
                        return (
                          <Chip
                            color="primary"
                            label={getTransportServiceName(tsuuid)}
                            key={tsuuid}
                          />
                        );
                      },
                    )}
                  </Grid>
                  <Grid item xs={12} className={classes.panelTitle}>
                    {t('provider.pricingModel')}
                  </Grid>
                  <Grid item xs={12}>
                    {getPricingModelName(
                      servicePricing.pricingModel.pricingModelUUID,
                    )}
                  </Grid>
                  <Grid item container spacing={2}>
                    <Grid item xs={12} className={classes.panelTitle}>
                      {t('provider.charges')}
                    </Grid>
                    {servicePricing.pricingModel.charges.map((ch) => {
                      return (
                        <Grid item container xs={12} key={ch.chargeTypeBC}>
                          <Grid item xs={4}>
                            {getChargeTypeName(ch.chargeTypeBC)}
                          </Grid>
                          {ch.functionParameters.map((fp, idx) => {
                            return (
                              <Grid item xs={4} key={idx}>
                                {stringUtils.camelCaseToWords(fp.name)}:{' '}
                                {fp.value}
                              </Grid>
                            );
                          })}
                          {ch.functionParameters.length === 1 && (
                            <Grid item xs={4} />
                          )}
                          {providerUtils.getPricingFunctionName(
                            pricingFunctions,
                            ch.pricingFunctionBC,
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                  <Grid item container spacing={2}>
                    <Grid item xs={12} className={classes.panelTitle}>
                      {t('provider.coverageAreas')}
                    </Grid>
                    {coverageAreas.map((coverageArea) => {
                      return (
                        <Grid
                          item
                          container
                          xs={12}
                          key={coverageArea.coverageAreaID}>
                          <Grid item xs={12}>
                            {coverageArea.coverageAreaName}
                          </Grid>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </StyledBox>
  );
}
